import { useState, useMemo } from 'react';

// Our Components
import ExportableReport from 'components/ExportableReport/ExportableReport';
import Loader from 'components/Loader/index';
import { TertiaryButton, SecondaryButton } from 'components/Button/Button';
import TextInput from 'components/Input/TextInput';
import Modal from 'components/Modal/Modal';

// Our Hooks
import useMutateSendAttachmentEmail from 'hooks/emails/useMutateSendAttachmentEmail';

// Our Utils
import { isSubmissionReady } from 'shared/utils';

import { usePDF } from 'react-to-pdf';
import isEmail from 'validator/lib/isEmail';

function SendInsightsReportModal({
	clientName,
	isModalOpen,
	isBurnDownSuccess,
	setIsModalOpen
}) {
	const [email, setEmail] = useState('');

	const sendAttachmentEmailMutation = useMutateSendAttachmentEmail();

	const { toPDF, targetRef } = usePDF({
		filename: `${clientName}_Insights_Report.pdf`,
		method: 'build'
	});

	const isLoading = false;
	const isFormReady = useMemo(
		() => isSubmissionReady([email]) && isEmail(email),
		[email]
	);

	const handleDownloadPDF = () => {
		toPDF()
			.then((pdfInstance) => {
				// Extract base64 string from the jsPDF instance
				const base64String = pdfInstance.output('datauristring');

				// Remove the "data:application/pdf;base64," prefix to get pure base64
				const pdfBase64 = base64String.split(',')[1];

				const payload = {
					message: {
						from_email: 'contact@sorafinance.com',
						from_name: 'Sora Finance',
						to: [
							{
								email,
								name: clientName
							}
						],
						subject: 'Your requested report.',
						html: `<h3>Here is the insights report for ${clientName}</h3>`,
						attachments: [
							{
								type: 'application/pdf',
								name: `${clientName}_Insights_Report.pdf`,
								content: pdfBase64
							}
						]
					}
				};

				// Use the custom hook to send the email with attachment
				sendAttachmentEmailMutation.mutate(payload, {
					onSuccess: () => {
						console.log('Email sent successfully');
					},
					onError: (error) => {
						console.error('Error sending email:', error);
					}
				});
			})
			.catch((error) => {
				console.error('Error generating PDF:', error);
			});
	};
	return (
		<Modal
			isOpen={isModalOpen}
			handleClose={(e) => {
				e.stopPropagation();
				setIsModalOpen(false);
			}}
			title="Liability Insights Report"
			subtitle="Please confirm you would like to send the report"
		>
			<TextInput
				label="Recipient Email Address"
				placeholder="Enter email address"
				helperText="Please enter a valid email"
				error={!isEmail(email)}
				value={email}
				onChange={setEmail}
				type="email"
				fullWidth
				sx={{ marginBottom: 2 }}
			/>
			{isLoading && <Loader />}
			{!isLoading && (
				<TertiaryButton
					onClick={(e) => {
						e.stopPropagation();
						handleDownloadPDF();
						setIsModalOpen(false);
					}}
					disabled={!isFormReady}
				>
					Send
				</TertiaryButton>
			)}
			<SecondaryButton
				sx={{ marginLeft: 2 }}
				onClick={(e) => {
					e.stopPropagation();
					setIsModalOpen(false);
				}}
			>
				Cancel
			</SecondaryButton>
			{isBurnDownSuccess && (
				<ExportableReport
					ref={targetRef}
					sx={{
						padding: 4,
						paddingTop: 6,
						paddingBottom: 8,
						position: 'fixed',
						left: '-100vw',
						top: 0,
						visibility: 'visible',
						width: '240mm',
						height: 'auto',
						display: 'block'
					}}
				/>
			)}
		</Modal>
	);
}

export default SendInsightsReportModal;
